import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { SERVICE_OPTIONS, type BookingStatus } from "./BookingDetailDialog";

const BookingCreateDialog = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState<string>(SERVICE_OPTIONS[0]);
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [price, setPrice] = useState("");
  const [passengers, setPassengers] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<BookingStatus>("pending");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(""); setPhone(""); setEmail(""); setService(SERVICE_OPTIONS[0]);
    setPickup(""); setDropoff(""); setBookingDate(""); setPrice(""); setMessage(""); setPassengers("");
    setStatus("pending");
  };

  const submit = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Le nom et le téléphone du client sont obligatoires.");
      return;
    }
    const trimmed = price.trim();
    const parsed = trimmed === "" ? null : Number(trimmed.replace(",", "."));
    if (trimmed !== "" && (parsed === null || Number.isNaN(parsed) || parsed < 0)) {
      toast.error("Prix invalide — saisissez un montant en MAD (ex. 450).");
      return;
    }
    const paxTrim = passengers.trim();
    const paxParsed = paxTrim === "" ? null : Number(paxTrim);
    if (paxTrim !== "" && (!Number.isInteger(paxParsed) || (paxParsed as number) < 1)) {
      toast.error("Nombre de passagers invalide — saisissez un nombre entier (ex. 4).");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("bookings").insert({
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim() || null,
      service,
      pickup_location: pickup.trim() || null,
      dropoff_location: dropoff.trim() || null,
      booking_date: bookingDate || null,
      transfer_price: parsed,
      passengers: paxParsed,
      message: message.trim() || null,
      status,
    });
    setSaving(false);
    if (error) {
      toast.error("Erreur: " + error.message);
      return;
    }
    toast.success("Réservation créée");
    reset();
    onCreated();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Nouvelle réservation</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="nb-name">Nom du client *</Label>
              <Input id="nb-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Karim Alaoui" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nb-phone">Téléphone *</Label>
              <Input id="nb-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+212 6 00 00 00 00" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nb-email">E-mail</Label>
            <Input id="nb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" />
          </div>

          <div className="space-y-1.5">
            <Label>Service *</Label>
            <Select value={service} onValueChange={setService}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="nb-pickup">Lieu de départ</Label>
              <Input id="nb-pickup" value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="Ex. Aéroport Marrakech" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nb-dropoff">Lieu d'arrivée</Label>
              <Input id="nb-dropoff" value={dropoff} onChange={(e) => setDropoff(e.target.value)} placeholder="Ex. Essaouira" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="nb-date">Date souhaitée</Label>
              <Input id="nb-date" type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nb-price">Prix du transfert (MAD)</Label>
              <Input id="nb-price" type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Ex. 450" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nb-pax">Nombre de passagers (facultatif)</Label>
            <Input id="nb-pax" type="number" min="1" step="1" value={passengers} onChange={(e) => setPassengers(e.target.value)} placeholder="Ex. 4" />
          </div>

          <div className="space-y-1.5">
            <Label>Statut</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as BookingStatus)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmé</SelectItem>
                <SelectItem value="completed">Terminé</SelectItem>
                <SelectItem value="cancelled">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nb-message">Message / notes</Label>
            <Textarea id="nb-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" /> Annuler
            </Button>
            <Button onClick={submit} disabled={saving}>
              <Plus className="w-4 h-4 mr-2" /> Créer la réservation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingCreateDialog;
